export const PUBLIC_PATHS = [
  "/",
  "/about",
  "/programs",
  "/sponsor",
  "/nominate",
  "/partner-schools",
  "/events",
  "/contact",
  "/womens-empowerment",
  "/ability-program",
] as const

export type PublicPath = (typeof PUBLIC_PATHS)[number]

export const SITE_NAME = "Redempta Development Foundation"

export type PageMeta = {
  title: string
  description: string
  /** Social card slug under /og, e.g. "about" -> /og/about.png */
  og: string
  /** Short display title for social cards; falls back to a title without the " — Redempta" suffix */
  cardTitle?: string
}

export const DEFAULT_META: PageMeta = {
  title: "Redempta Development Foundation",
  description:
    "Scholarships, mentorship, and opportunity for bright children — no child's future decided by a school fee.",
  og: "home",
}

export const PAGE_META: Record<PublicPath, PageMeta> = {
  "/": {
    ...DEFAULT_META,
    title: "Redempta Development Foundation — Putting every child through school",
    cardTitle: "Every child through school.",
  },
  "/about": {
    title: "About — Redempta",
    description:
      "It started with classmates disappearing from school over fees families couldn't pay. We built the foundation that keeps them in the room.",
    og: "about",
    cardTitle: "No child is a lost cause.",
  },
  "/programs": {
    title: "Programs — Redempta",
    description:
      "Scholarships open the classroom door; the Learning Hub and Skills Center make sure scholars leave ready — for university, for work, for something of their own.",
    og: "programs",
    cardTitle: "One long game.",
  },
  "/sponsor": {
    title: "Sponsor a Child — Redempta",
    description:
      "Pick a path: a child through school, a woman building a business, or a person living with disability getting devices, therapy, and education. Real updates included.",
    og: "sponsor",
    cardTitle: "Fund a future.",
  },
  "/nominate": {
    title: "Nominate a Child — Redempta",
    description:
      "Tell us about them. Every nomination is verified with parents or guardians first — that's how we protect the family and the trust of our sponsors.",
    og: "nominate",
    cardTitle: "Know a child?",
  },
  "/partner-schools": {
    title: "Partner Schools — Redempta",
    description:
      "A small, carefully chosen group stands behind our scholars for the long term — because a seat is only the start.",
    og: "partner-schools",
    cardTitle: "In it for the long term.",
  },
  "/events": {
    title: "Events — Redempta",
    description:
      "Workshops for scholars, gatherings for the women in our communities, and the moments that bring the whole Redempta family together.",
    og: "events",
    cardTitle: "What's next.",
  },
  "/contact": {
    title: "Contact — Redempta",
    description:
      "Every partnership starts with a short inquiry. Tell us what you have in mind and we'll walk you through matching, funding, and updates.",
    og: "contact",
    cardTitle: "Let's build together.",
  },
  "/womens-empowerment": {
    title: "Women's Empowerment — Redempta",
    description:
      "Mothers, guardians, and aunts in our scholars' lives get training, micro-grants, and coaching to run their small businesses — so home stays steady.",
    og: "womens-empowerment",
    cardTitle: "Behind every scholar.",
  },
  "/ability-program": {
    title: "Ability Program — Redempta",
    description:
      "Devices, therapy, tuition, and skills training for people living with physical disabilities — from young children to university students to working adults.",
    og: "ability-program",
    cardTitle: "Opportunity for all.",
  },
}

const PAGE_META_BY_OG = new Map<string, PageMeta>(
  Object.values(PAGE_META).map((meta) => [meta.og, meta]),
)

export function truncate(text: string, max: number): string {
  const clean = text.replace(/\s+/g, " ").trim()
  if (clean.length <= max) return clean
  const cut = clean.slice(0, max - 1)
  const lastSpace = cut.lastIndexOf(" ")
  return `${(lastSpace > max * 0.5 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`
}

export function matchPageMeta(pathname: string): { path: PublicPath; meta: PageMeta } | null {
  const normalized = pathname.replace(/\/+$/, "") || "/"
  const match = PUBLIC_PATHS.find((path) => path === normalized)
  return match ? { path: match, meta: PAGE_META[match] } : null
}

export function pageMetaByOgSlug(slug: string): PageMeta | null {
  return PAGE_META_BY_OG.get(slug) ?? null
}

export function ogImagePath(og: string, version?: string | number): string {
  const path = `/og/${og}.png`
  return version === undefined ? path : `${path}?v=${encodeURIComponent(String(version))}`
}

export type EventMetaInput = {
  id: string
  title: string
  description: string | null
  date: Date
  venue: string | null
  updatedAt?: Date
}

const EVENT_DATE_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
})

export function eventDateLabel(date: Date): string {
  return EVENT_DATE_FORMAT.format(date)
}

export function buildEventMeta(event: EventMetaInput): PageMeta {
  const when = [eventDateLabel(event.date), event.venue].filter(Boolean).join(" · ")
  const description = event.description ? `${when} — ${event.description}` : when
  return {
    title: `${event.title} — Redempta`,
    description: truncate(description, 160),
    og: `events/${event.id}`,
  }
}
